import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { BehaviorSubject, catchError, filter, finalize, Observable, switchMap, take, throwError } from 'rxjs';
import { LoadingService } from '../loading/loading.service';
import { ToastService } from '../toast/toast.service';
import { Constants } from 'src/app/constants/constants';
import { Router } from '@angular/router';
import { StorageService } from '../storage/storage.service';
import { UserMetadata } from 'src/app/models/UserMetadata';
import { AuthService, REFRESH_TOKEN_API_URL } from '../auth/auth.service';

export const BYPASS_INTERCEPTOR = new HttpContextToken(() => false);
export const SHOW_API_ERROR_DIALOG = new HttpContextToken(() => false);

@Injectable()
export class InterceptorInterceptor implements HttpInterceptor {
  private totalRequests: number = 0;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor(
    private loadingService: LoadingService,
    private toastService: ToastService,
    private router: Router,
    private authService: AuthService,
    private storageService: StorageService,
  ) {};

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const userMetadata: UserMetadata = this.storageService.getItem('user_metadata');

    if(!request.context.get(BYPASS_INTERCEPTOR)){
      this.totalRequests++;
      this.loadingService.setLoading(true);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if(error.status === 401){
          if(request.url.includes(REFRESH_TOKEN_API_URL)){
            this.router.navigateByUrl('/login');
          }
          return this.handle401Error(userMetadata, request, next);
        } else if(error.status === 400 && error.error.message === 'Access Restricted!'){
          // do nothing
        } else if(!request.context.get(SHOW_API_ERROR_DIALOG)){
          this.toastService.showToast(Constants.ERROR, Constants.ERROR_CAPS, error.error.message);
        }

        return throwError(() => new ErrorEvent('Error', { error: error.error }));
      }),
      finalize(() => {
        if(!request.context.get(BYPASS_INTERCEPTOR)){
          this.totalRequests--;
          if (this.totalRequests == 0) {
            this.loadingService.setLoading(false);
          }
        }
      })
    );
  }

  private handle401Error(
    userMetadata: UserMetadata | null,
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!userMetadata?.refresh_token) {
      this.router.navigateByUrl('/login');
      return throwError(() => new Error('Missing refresh token'));
    }
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      return this.authService.getRefreshToken({ refresh_token: userMetadata.refresh_token }).pipe(
        switchMap((response: any) => {
          const newAccessToken = response?.data?.access_token;
          if (!newAccessToken) throw new Error('No access token in refresh response');
          userMetadata.access_token = newAccessToken;
          this.storageService.setItem('user_metadata', userMetadata);
          this.refreshTokenSubject.next(newAccessToken);
          return next.handle(
            request.clone({ setHeaders: { Authorization: `Bearer ${newAccessToken}` } })
          );
        }),
        catchError((err) => {
          this.router.navigateByUrl('/login');
          return throwError(() => err);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    }
    // Refresh already running: wait for it
    return this.refreshTokenSubject.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap((token) =>
        next.handle(request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }))
      )
    );
  }
}
